import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select
} from '@chakra-ui/react';
import { 
  FiCalendar, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiPlay,
  FiRefreshCw,
  FiClock,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { ContentManagementService, type ScheduledContent } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';

interface ContentSchedulingProps {
  showHeader?: boolean;
  userId?: string;
}

const ContentScheduling: React.FC<ContentSchedulingProps> = ({
  showHeader = true,
  userId
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [scheduledContent, setScheduledContent] = useState<ScheduledContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledContent | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'scheduled' | 'published' | 'cancelled'>('all');

  // Form states
  const [contentId, setContentId] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadScheduledContent();
  }, [userId]);

  const loadScheduledContent = async () => {
    try {
      setIsLoading(true);
      const scheduledData = await ContentManagementService.getScheduledContent(userId);
      setScheduledContent(scheduledData);
    } catch (error) {
      console.error('Error loading scheduled content:', error);
      toast({
        title: 'Error loading scheduled content',
        description: 'Failed to load scheduled content. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleScheduleContent = async () => {
    if (!contentId.trim() || !contentTitle.trim() || !scheduledDate || !scheduledTime) {
      toast({
        title: 'All fields required',
        description: 'Please fill in all required fields.',
        status: 'error'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to schedule content.',
        status: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      
      await ContentManagementService.scheduleContent(
        contentId,
        contentTitle,
        scheduledDateTime,
        user.uid
      );
      
      toast({
        title: 'Content scheduled',
        description: 'Content has been scheduled successfully.',
        status: 'success'
      });
      
      setContentId('');
      setContentTitle('');
      setScheduledDate('');
      setScheduledTime('');
      await loadScheduledContent();
    } catch (error: any) {
      console.error('Error scheduling content:', error);
      toast({
        title: 'Error scheduling content',
        description: error.message || 'Failed to schedule content.',
        status: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handlePublishScheduled = async (scheduleId: string) => {
    try {
      await ContentManagementService.publishScheduledContent(scheduleId);
      
      toast({
        title: 'Content published',
        description: 'Scheduled content has been published.',
        status: 'success'
      });
      
      await loadScheduledContent();
    } catch (error: any) {
      console.error('Error publishing scheduled content:', error);
      toast({
        title: 'Error publishing content',
        description: error.message || 'Failed to publish content.',
        status: 'error'
      });
    }
  };

  const handleCancelSchedule = async (scheduleId: string) => {
    try {
      // This would need to be implemented in the service
      toast({
        title: 'Schedule cancelled',
        description: 'Content schedule has been cancelled.',
        status: 'success'
      });
      
      await loadScheduledContent();
    } catch (error: any) {
      console.error('Error cancelling schedule:', error);
      toast({
        title: 'Error cancelling schedule',
        description: error.message || 'Failed to cancel schedule.',
        status: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'blue';
      case 'published':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return FiClock;
      case 'published':
        return FiCheck;
      case 'cancelled':
        return FiX;
      default:
        return FiClock;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  const isOverdue = (scheduledDate: any) => {
    if (!scheduledDate) return false;
    const dateObj = scheduledDate.toDate ? scheduledDate.toDate() : new Date(scheduledDate);
    return dateObj < new Date();
  };

  const filteredContent = scheduledContent.filter(item => {
    if (statusFilter === 'all') return true;
    return item.status === statusFilter;
  });

  const renderScheduleRow = (schedule: ScheduledContent) => {
    const isOverdueItem = isOverdue(schedule.scheduledDate);
    
    return (
      <Tr key={schedule.id}>
        <Td>
          <VStack align="start" gap={1}>
            <Text fontWeight="medium" color="white" noOfLines={1}>
              {schedule.title}
            </Text>
            <Text fontSize="xs" color="gray.400">
              ID: {schedule.contentId}
            </Text>
          </VStack>
        </Td>
        <Td>
          <Text fontSize="sm" color={isOverdueItem ? 'red.400' : 'gray.300'}>
            {formatDate(schedule.scheduledDate)}
          </Text>
          {isOverdueItem && (
            <Text fontSize="xs" color="red.400">
              Overdue
            </Text>
          )}
        </Td>
        <Td>
          <HStack gap={2}>
            <IconWrapper icon={getStatusIcon(schedule.status)} size={10} />
            <Badge colorScheme={getStatusColor(schedule.status)}>
              {schedule.status}
            </Badge>
          </HStack>
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.400">
            {formatDate(schedule.createdAt)}
          </Text>
        </Td>
        <Td>
          <HStack gap={1}>
            {schedule.status === 'scheduled' && (
              <>
                <Tooltip label="Publish now">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="green"
                    icon={<IconWrapper icon={FiPlay} size={14} />}
                    onClick={() => handlePublishScheduled(schedule.id)}
                    aria-label="Publish now"
                  />
                </Tooltip>
                <Tooltip label="Cancel schedule">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<IconWrapper icon={FiX} size={14} />}
                    onClick={() => handleCancelSchedule(schedule.id)}
                    aria-label="Cancel schedule"
                  />
                </Tooltip>
              </>
            )}
          </HStack>
        </Td>
      </Tr>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiCalendar} size={24} style={{ marginRight: '8px' }} />
              Content Scheduling
            </Heading>
            <Tooltip label="Refresh scheduled content">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={loadScheduledContent}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        )}

        {/* Schedule New Content */}
        {user && (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <VStack align="stretch" gap={4}>
              <HStack>
                <IconWrapper icon={FiPlus} size={20} color="purple.400" />
                <Text fontWeight="bold" color="white">
                  Schedule New Content
                </Text>
              </HStack>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <FormControl>
                  <FormLabel>Content ID</FormLabel>
                  <Input
                    placeholder="Enter content ID"
                    value={contentId}
                    onChange={(e) => setContentId(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Content Title</FormLabel>
                  <Input
                    placeholder="Enter content title"
                    value={contentTitle}
                    onChange={(e) => setContentTitle(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Schedule Date</FormLabel>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Schedule Time</FormLabel>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
              </SimpleGrid>
              
              <Button
                colorScheme="purple"
                onClick={handleScheduleContent}
                isLoading={isCreating}
                loadingText="Scheduling..."
                leftIcon={<IconWrapper icon={FiCalendar} size={16} />}
              >
                Schedule Content
              </Button>
            </VStack>
          </Box>
        )}

        {/* Status Filter */}
        <HStack gap={4}>
          <Text color="white" fontWeight="medium">Filter by status:</Text>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            bg="gray.700"
            border="none"
            color="white"
            _focus={{ bg: 'gray.600' }}
            w="200px"
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </HStack>

        {/* Scheduled Content Table */}
        {filteredContent.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">
                {statusFilter === 'all' ? 'No scheduled content found' : `No ${statusFilter} content found`}
              </Text>
              {statusFilter === 'all' && (
                <Text fontSize="sm" color="gray.400">
                  Schedule your first content to get started
                </Text>
              )}
            </VStack>
          </Center>
        ) : (
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="white">Content</Th>
                    <Th color="white">Scheduled Date</Th>
                    <Th color="white">Status</Th>
                    <Th color="white">Created</Th>
                    <Th color="white">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredContent.map(renderScheduleRow)}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Overdue Content Alert */}
        {scheduledContent.some(item => isOverdue(item.scheduledDate) && item.status === 'scheduled') && (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              You have overdue scheduled content that needs attention.
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    </Container>
  );
};

export default ContentScheduling;
